export type ErrorSeverity = 'low' | 'medium' | 'high' | 'critical';

export interface RecorderError {
  code: string;
  message: string;
  userMessage: string;
  severity: ErrorSeverity;
  timestamp: Date;
  recoverable: boolean;
  recoveryAction?: () => void;
}

type ErrorMonitorPayload = {
  message: string;
  context?: string;
  stack?: string;
  severity?: ErrorSeverity;
  timestamp?: Date;
};

type ErrorMonitor = (payload: ErrorMonitorPayload) => void | Promise<void>;

let registeredMonitor: ErrorMonitor | null = null;

export const registerErrorMonitor = (monitor: ErrorMonitor) => {
  registeredMonitor = monitor;
};

class ErrorHandlingService {
  // Log error to console with proper formatting
  logError(error: Error | string, context?: string): void {
    const errorMessage = typeof error === 'string' ? error : error.message;
    const stack = error instanceof Error ? error.stack : undefined;
    
    console.error(`[VideoRecorder]${context ? ` [${context}]` : ''} ${errorMessage}`);
    if (stack) console.error(stack);

    // Forward to registered monitoring sink if available
    try {
      if (registeredMonitor) {
        registeredMonitor({
          message: errorMessage,
          context,
          stack,
          severity: 'medium',
          timestamp: new Date()
        });
      }
    } catch {
      // Never let monitoring failures break the app
    }
  }
  
  // Format user-friendly error messages
  getUserFriendlyMessage(errorType: string): string {
    const errorMessages: Record<string, string> = {
      'camera_access_denied': 'Camera access was denied. Please check your browser permissions.',
      'screen_access_denied': 'Screen sharing was denied. Please try again and select a screen to share.',
      'recording_failed': 'Failed to start recording. Please try again.',
      'empty_recording': 'No video data was recorded. Please check your camera and microphone.',
      'media_device_error': 'There was a problem with your camera or microphone.',
      'screen_sharing_ended': 'Screen sharing was ended. Your recording has been saved.',
      'default': 'An unexpected error occurred.'
    };
    
    return errorMessages[errorType] || errorMessages.default;
  }
  
  // Create standardized error object
  createError(
    code: string,
    originalError: Error | string,
    severity: ErrorSeverity = 'medium',
    recoverable: boolean = true,
    recoveryAction?: () => void
  ): RecorderError {
    return {
      code,
      message: typeof originalError === 'string' ? originalError : originalError.message,
      userMessage: this.getUserFriendlyMessage(code),
      severity,
      timestamp: new Date(),
      recoverable,
      recoveryAction
    };
  }
}

export const errorHandlingService = new ErrorHandlingService();
